import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { matchPath, useLocation, useNavigate, useParams } from 'react-router';
import { parse, stringify } from 'query-string';

import { AVAILABLE_CHIP_TAGS, SYSTEM_TAG_IAB } from '../../constants/label';
import { videoPropTypes } from '../../constants/propTypes';
import {
  QUERY_SEARCH_KEY,
  QUERY_SELECTED_CHIP_IAB_KEY,
  QUERY_SELECTED_CHIP_KEY,
  QUERY_SELECTED_CHIP_TAG_KEY,
} from '../../constants/queryParams';
import { ALL_EXISTING_ROUTES } from '../../constants/routePath';
import { MAIN_PLAYER_INSTANCE_KEY } from '../Player/constants';

import {
  arrayDifference,
  findCaseInsensitive,
  getIntersection,
  getUniqueStringsWithRepeats,
  includesCaseInsensitive,
} from '../../helpers/array';
import { processHistoryObject } from '../../helpers/history';
import logger from '../../helpers/logger';
import { safetyJsonParse } from '../../helpers/request';
import * as commonSelectors from '../../redux/selectors/common';
import { usePlayerWidget } from '../Player/helpers';

import { BrowserLink, Chip, Tab, Tabs } from '@/mui/components';

import styles from './Chips.module.scss';

const CHIP_ALL_LABEL = 'All';
const TYPE_ORDER = AVAILABLE_CHIP_TAGS.reduce((obj, type, index) => ({ ...obj, [type]: index }), {});

const sortEntitiesByTypeAndValue = (a, b) => {
  if (TYPE_ORDER[a.type] === TYPE_ORDER[b.type]) {
    return a.value.localeCompare(b.value);
  }
  return TYPE_ORDER[a.type] - TYPE_ORDER[b.type];
};

function Chips({ className = '', ...props }) {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const watchId = useSelector(commonSelectors.idSelector);
  const fixedTags = useSelector(commonSelectors.fixedTagsSelector);
  const excludedTags = useSelector(commonSelectors.excludedTagsSelector);
  const tagCount = useSelector(commonSelectors.tagCountSelector);
  const tagMin = useSelector(commonSelectors.tagMinSelector);
  const tagMax = useSelector(commonSelectors.tagMaxSelector);
  const LWP = usePlayerWidget(MAIN_PLAYER_INSTANCE_KEY);

  const excludedTagsLowerCase = excludedTags.map((chip) => chip.toLowerCase());

  const currentRoute = ALL_EXISTING_ROUTES.find((route) => matchPath(route, location.pathname));
  const queryChipObj = parse(location.search)[QUERY_SELECTED_CHIP_KEY];
  const { [QUERY_SELECTED_CHIP_TAG_KEY]: queryChip = '', [QUERY_SELECTED_CHIP_IAB_KEY]: queryChipIabIds } =
    safetyJsonParse(queryChipObj);

  const [referredTag, setReferredTag] = useState(queryChip);

  const videoEntities = props.videos.reduce((acc, video) => {
    video.videoEntities.forEach((videoEntity) => {
      if (!excludedTagsLowerCase.includes(videoEntity.value.toLowerCase())) {
        acc.push(videoEntity);
      }
    });

    return acc;
  }, []);

  const iabEntities = videoEntities.reduce((acc, videoEntity) => {
    if (videoEntity.type === SYSTEM_TAG_IAB) {
      const key = videoEntity.value.toLowerCase();

      if (!acc[key]) {
        acc[key] = [videoEntity.id];
      } else if (!acc[key].includes(videoEntity.id)) {
        acc[key].push(videoEntity.id);
      }
    }
    return acc;
  }, {});

  const getQueryWithTag = (tag) => ({
    ...parse(location.search),
    [QUERY_SELECTED_CHIP_KEY]: tag
      ? JSON.stringify({
          [QUERY_SELECTED_CHIP_TAG_KEY]: tag,
          [QUERY_SELECTED_CHIP_IAB_KEY]: (tag && iabEntities[tag.toLowerCase()]) || undefined,
        })
      : undefined,
  });

  const getChipUrl = (tag, isSystem) => {
    const query = stringify(getQueryWithTag(tag, isSystem));
    const queryPrefix = query ? `?${query}` : '';

    return `${location.pathname}${queryPrefix}`;
  };

  let chipsToShow = [];
  let videoTags = [];

  if (props.videos.length >= tagCount || queryChip || referredTag) {
    videoTags = props.videos
      .reduce((acc, video) => {
        const sortedVideoEntities = video.videoEntities
          .slice()
          .filter((videoEntity) => !excludedTagsLowerCase.includes(videoEntity.value.toLowerCase()))
          .sort(sortEntitiesByTypeAndValue);

        const uniqueValues = {};
        const uniqueVideoEntities = [];

        sortedVideoEntities.forEach((obj) => {
          if (!uniqueValues[obj.value.toLowerCase()]) {
            uniqueValues[obj.value.toLowerCase()] = true;
            uniqueVideoEntities.push(obj);
          }
        });

        return acc.concat(uniqueVideoEntities);
      }, [])
      .sort(sortEntitiesByTypeAndValue)
      .map((entity) => entity.value);

    const availableFixedTags = getIntersection(fixedTags, videoTags);
    const uniqueVideoTags = getUniqueStringsWithRepeats(videoTags, tagCount);
    const uniqueExceptFixeTags = arrayDifference(uniqueVideoTags, availableFixedTags);

    chipsToShow = availableFixedTags.concat(uniqueExceptFixeTags).slice(0, tagMax);

    if (referredTag && !includesCaseInsensitive(chipsToShow, referredTag)) {
      chipsToShow.splice(0, 0, referredTag);
      chipsToShow = chipsToShow.slice(0, tagMax);
    }
  }

  const chipsWithTypes = chipsToShow.reduce((acc, chip) => {
    acc[chip] = [
      ...new Set(
        videoEntities
          .filter((videoEntity) => videoEntity.value.toLowerCase() === chip.toLowerCase())
          .map((videoEntity) => videoEntity.type),
      ),
    ];
    return acc;
  }, {});

  logger.log(
    'CHIPS_LOGGING',
    '\nfixedTags -',
    fixedTags,
    '\nexcludedTags -',
    excludedTags,
    '\nqueryChip -',
    queryChip,
    '\nqueryChipIabIds -',
    queryChipIabIds,
    '\nreferredTag -',
    referredTag,
    '\nchipsToShow -',
    chipsToShow,
    '\nchipsWithTypes -',
    chipsWithTypes,
    '\niabEntities -',
    iabEntities,
  );

  useEffect(() => {
    setReferredTag(queryChip);
  }, [currentRoute]);

  useEffect(() => {
    if (queryChip) {
      const queryChipFromChipsToShow = findCaseInsensitive(chipsToShow, queryChip);
      let queryChipFromVideoTags;
      if (!queryChipFromChipsToShow) {
        queryChipFromVideoTags = findCaseInsensitive(videoTags, queryChip);
      }

      if (chipsToShow.length && chipsToShow.length >= tagMin && queryChipFromChipsToShow) {
        // removed from redux state
        // dispatch(setSelectedChipAction(queryChipFromChipsToShow));
      } else if (videoTags.length && queryChipFromVideoTags) {
        if (!referredTag) {
          setReferredTag(queryChipFromVideoTags);
        }
      } else if (props.videos.length) {
        setReferredTag(null);
        navigate(
          processHistoryObject({
            search: getQueryWithTag(''),
          }),
          { replace: true },
        );
      }
    }
  }, [props.videos.map(({ videoId }) => videoId).join('|'), queryChip, queryChipIabIds]);

  return chipsToShow.length >= tagMin || referredTag ? (
    <div className={className}>
      <Tabs
        size="small"
        variant="scrollable"
        value={queryChip.toLowerCase()}
        allowScrollButtonsMobile
        classes={{
          root: styles.TabsRoot,
          indicator: styles.TabsIndicator,
        }}
        aria-label="Video compilations by subject"
      >
        {[''].concat(chipsToShow).map((tag, index) => (
          <Tab
            key={tag}
            label={
              <Chip
                label={!index ? CHIP_ALL_LABEL : tag}
                to={getChipUrl(tag)}
                component={BrowserLink}
                size="small"
                color="primary"
                variant={queryChip.toLowerCase() === tag.toLowerCase() ? 'filled' : 'outlined'}
                className={styles.Chip}
                onClick={() => {
                  LWP.sendStatsChipClicked(
                    watchId,
                    currentRoute,
                    params.channel,
                    parse(location.search)[QUERY_SEARCH_KEY],
                    !index ? CHIP_ALL_LABEL : tag,
                    index,
                    chipsWithTypes[tag]?.join('|'),
                  );
                }}
              />
            }
            value={tag.toLowerCase()}
            disableRipple
            className={styles.Tab}
          />
        ))}
      </Tabs>
    </div>
  ) : null;
}

Chips.propTypes = {
  videos: PropTypes.arrayOf(videoPropTypes).isRequired,
  className: PropTypes.string,
};

export default Chips;
